import './CliDock.less';

import {
  BorderOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  CodeOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  LineOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import {
  animate,
  AnimatePresence,
  motion,
  MotionStyle,
  useMotionValue,
} from 'motion/react';
import {
  PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import CliSessionPanel from './CliSessionPanel';
import CliSessionStatus from './CliSessionStatus';
import {
  CLI_DOCK_MAX_INSET,
  CLI_DOCK_MIN_HEIGHT,
  CLI_DOCK_MINIMIZED,
  CLI_DOCK_SHRUNK_WIDTH,
  closeCliDock,
  closeCliSession,
  focusCliSession,
  setCliDockHeight,
  setCliPicking,
  toggleCliDockMaximized,
  toggleCliDockMinimized,
  toggleCliDockShrunk,
  useCliDock,
} from './cliSessionStore';
import { useSurfaceLift } from 'utils/surfaceStack';
import { useCliTerminal } from 'hooks/cliTerminalQueries';

const DOCK_MOTION = {
  type: 'spring' as const,
  stiffness: 340,
  damping: 38,
  mass: 0.9,
};
const CHIP_MOTION = {
  type: 'spring' as const,
  stiffness: 520,
  damping: 40,
  mass: 0.6,
};
const TAB_MOTION = {
  type: 'spring' as const,
  stiffness: 520,
  damping: 42,
  mass: 0.7,
};

function CliDock() {
  const {
    sessions,
    active,
    minimized,
    shrunk,
    maximized,
    height,
    inset,
    done,
  } = useCliDock();
  const { lift, raise } = useSurfaceLift('cli-dock');

  // Folded and pulled aside: a pill holding the way back and nothing else.
  const compact = minimized && shrunk;

  // The two sizes the user cannot drag are read off the window.
  const [viewport, setViewport] = useState(() => ({
    w: window.innerWidth,
    h: window.innerHeight,
  }));
  useEffect(() => {
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const shownHeight = minimized
    ? CLI_DOCK_MINIMIZED
    : maximized
      ? viewport.h - CLI_DOCK_MAX_INSET
      : height;
  const shownWidth = compact ? CLI_DOCK_SHRUNK_WIDTH : viewport.w - inset;

  // A motion value, not an `animate` prop: the resize drag writes it straight, and a prop would spring back.
  const shownHeightValue = useMotionValue(shownHeight);
  useEffect(() => {
    const settle = animate(shownHeightValue, shownHeight, DOCK_MOTION);
    return () => settle.stop();
  }, [shownHeightValue, shownHeight]);

  // Width too: CSS cannot tween between a full-bleed edge and a fixed width.
  const shownWidthValue = useMotionValue(shownWidth);
  useEffect(() => {
    const settle = animate(shownWidthValue, shownWidth, DOCK_MOTION);
    return () => settle.stop();
  }, [shownWidthValue, shownWidth]);

  // Committed to the store once, on release, rather than on every pointer move.
  const dragStart = useRef<{ y: number; height: number } | null>(null);
  const dragged = useRef(height);

  const onResizeStart = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (minimized || maximized) return;
    dragStart.current = { y: event.clientY, height };
    dragged.current = height;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onResizeMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    const next =
      dragStart.current.height + (dragStart.current.y - event.clientY);
    dragged.current = Math.max(
      CLI_DOCK_MIN_HEIGHT,
      Math.min(window.innerHeight - CLI_DOCK_MAX_INSET, next),
    );
    shownHeightValue.set(dragged.current);
  };

  const onResizeEnd = () => {
    if (!dragStart.current) return;
    dragStart.current = null;
    setCliDockHeight(dragged.current);
  };

  const activeCli = useCliTerminal({
    clusterName: active ?? '',
    enabled: Boolean(active),
  });

  const openPicker = () => setCliPicking(true);

  const restore = () => {
    if (done[0]) focusCliSession(done[0]);
    else toggleCliDockMinimized();
  };

  return (
    <motion.div
      className={`CliDock${minimized ? ' is-minimized' : ''}${compact ? ' is-shrunk' : ''}${maximized ? ' is-maximized' : ''}`}
      style={
        {
          left: inset,
          width: shownWidthValue,
          height: shownHeightValue,
          '--surface-lift': lift,
        } as MotionStyle
      }
      initial={{ y: '100%', opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: '100%', opacity: 0 }}
      transition={DOCK_MOTION}
      onPointerDownCapture={raise}
      role="region"
      aria-label="Incorta CLI"
    >
      <div
        className="CliDock__resize"
        onPointerDown={onResizeStart}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeEnd}
        onPointerCancel={onResizeEnd}
        aria-hidden
      />

      <div className="CliDock__header">
        <span
          className={`CliDock__title${compact && done.length ? ' has-news' : ''}`}
        >
          <CodeOutlined />
          CLI
        </span>

        {!compact && (
          <div
            className="CliDock__sessions"
            role="tablist"
            aria-label="CLI sessions"
          >
            <AnimatePresence initial={false} mode="popLayout">
              {sessions.map(name => (
                <motion.div
                  key={name}
                  layout
                  initial={{ opacity: 0, y: 6, scale: 0.94 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 6, scale: 0.94 }}
                  transition={TAB_MOTION}
                  className={`CliDock__session-tab${name === active ? ' is-active' : ''}${done.includes(name) ? ' has-news' : ''}`}
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={name === active}
                    className="CliDock__session-name"
                    onClick={() => focusCliSession(name)}
                  >
                    {name}
                  </button>
                  <button
                    type="button"
                    className="CliDock__session-close"
                    aria-label={`Close the CLI session on ${name}`}
                    onClick={() => closeCliSession(name)}
                  >
                    <CloseOutlined />
                  </button>
                  {name === active && !minimized && (
                    <motion.span
                      layoutId="cli-session-rule"
                      className="CliDock__session-rule"
                      transition={TAB_MOTION}
                    />
                  )}
                </motion.div>
              ))}
            </AnimatePresence>

            <Tooltip title="Open a session on another cluster you hold a token for">
              <button
                type="button"
                className="CliDock__session-add"
                aria-label="Open a new CLI session"
                onClick={() => openPicker()}
              >
                <PlusOutlined />
              </button>
            </Tooltip>
          </div>
        )}

        <AnimatePresence initial={false}>
          {!compact && Boolean(done.length) && (
            <motion.button
              key="done"
              type="button"
              className="CliDock__done"
              initial={{ opacity: 0, x: 12, scale: 0.94 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 12, scale: 0.94 }}
              transition={CHIP_MOTION}
              onClick={restore}
            >
              <CheckCircleOutlined />
              Command done running
              <span className="CliDock__done-where">{done.join(', ')}</span>
            </motion.button>
          )}
        </AnimatePresence>

        {activeCli.session && active && !minimized && !compact && (
          <CliSessionStatus session={activeCli.session} />
        )}

        <div className="CliDock__controls">
          <Tooltip title={minimized ? 'Restore' : 'Minimize'}>
            <button
              type="button"
              className="CliDock__control"
              onClick={() => toggleCliDockMinimized()}
              aria-label={minimized ? 'Restore' : 'Minimize'}
            >
              <LineOutlined />
            </button>
          </Tooltip>
          {!compact && (
            <Tooltip title={maximized ? 'Restore size' : 'Maximize'}>
              <button
                type="button"
                className="CliDock__control"
                onClick={() => toggleCliDockMaximized()}
                aria-label="Maximize"
              >
                <BorderOutlined />
              </button>
            </Tooltip>
          )}
          {!compact && (
            <Tooltip title="Close the CLI">
              <button
                type="button"
                className="CliDock__control"
                onClick={() => closeCliDock()}
                aria-label="Close"
              >
                <CloseOutlined />
              </button>
            </Tooltip>
          )}

          {/* Folded, the dock covers the page's footer; this pulls it aside. */}
          {minimized && (
            <Tooltip
              title={
                shrunk ? 'Bring the CLI back across' : 'Move the CLI aside'
              }
            >
              <button
                type="button"
                className="CliDock__control CliDock__aside"
                onClick={() => toggleCliDockShrunk()}
                aria-label={
                  shrunk ? 'Bring the CLI back across' : 'Move the CLI aside'
                }
              >
                {shrunk ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Hidden, never unmounted: a folded dock is still a running command. */}
      <div className="CliDock__body" hidden={minimized}>
        {sessions.map(name => (
          <div key={name} className="CliDock__pane" hidden={name !== active}>
            <CliSessionPanel
              clusterName={name}
              active={name === active}
              outOfSight={minimized}
            />
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default CliDock;
